// @systeme: interface
// Les formules et leurs tarifs. Un nouveau prix crée un nouveau prix chez Stripe et
// prévient les abonnées : il ne s'applique à elles qu'un mois plus tard (DEC-021).
import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { PlanAdmin } from '@foyer/shared';
import { Chargement } from '../../composants/Chargement';
import { useMessages } from '../../composants/Messages';
import { api } from '../../lib/api';

function CartePlan({ plan, onMaj }: { plan: PlanAdmin; onMaj: (p: PlanAdmin) => void }) {
  const { dire, direErreur } = useMessages();
  const [nom, setNom] = useState(plan.nom);
  const [description, setDescription] = useState(plan.description);
  const [prix, setPrix] = useState(plan.prixTtc.toFixed(2));
  const [vocaux, setVocaux] = useState(String(plan.quotas.vocaux_mois));
  const [photos, setPhotos] = useState(String(plan.quotas.photos_mois));
  const [documentsPhoto, setDocumentsPhoto] = useState(plan.droits.documents_photo);
  const [actif, setActif] = useState(plan.actif);
  const [confirmerPrix, setConfirmerPrix] = useState(false);
  const prixChange = Number(prix.replace(',', '.')).toFixed(2) !== plan.prixTtc.toFixed(2);

  async function enregistrer() {
    const nouveauPrix = Number(prix.replace(',', '.'));
    if (prixChange && !confirmerPrix && plan.abonneesAncienTarif + 1 > 0) {
      setConfirmerPrix(true);
      return;
    }
    try {
      const maj = await api.admin.modifierPlan(plan.cle, {
        nom,
        description,
        ...(prixChange && Number.isFinite(nouveauPrix) ? { prixTtc: nouveauPrix } : {}),
        quotas: { vocaux_mois: Number(vocaux) || 0, photos_mois: Number(photos) || 0 },
        droits: { documents_photo: documentsPhoto },
        actif,
      });
      onMaj(maj);
      setPrix(maj.prixTtc.toFixed(2));
      setConfirmerPrix(false);
      dire(
        prixChange ? 'Tarif enregistré. Les abonnées concernées sont prévenues.' : 'C’est réglé.',
      );
    } catch (e) {
      direErreur(e);
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="h2" component="h2">
          {plan.nom}{' '}
          <Typography component="span" variant="body2" color="text.secondary">
            ({plan.cle})
          </Typography>
        </Typography>
        <TextField label="Nom" size="small" value={nom} onChange={(e) => setNom(e.target.value)} />
        <TextField
          label="Description (une phrase)"
          size="small"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        {plan.cle !== 'essai' && (
          <TextField
            label="Prix mensuel TTC (€)"
            size="small"
            value={prix}
            onChange={(e) => setPrix(e.target.value)}
            inputProps={{ inputMode: 'decimal' }}
            helperText={
              plan.stripePret
                ? plan.abonneesAncienTarif > 0
                  ? `${plan.abonneesAncienTarif} abonnée(s) paient encore un ancien tarif.`
                  : 'Prix en place chez Stripe.'
                : 'Pas encore de prix chez Stripe : donner un prix le crée.'
            }
          />
        )}
        <Stack direction="row" spacing={1}>
          <TextField
            label="Vocaux par mois"
            size="small"
            value={vocaux}
            onChange={(e) => setVocaux(e.target.value)}
            inputProps={{ inputMode: 'numeric' }}
          />
          <TextField
            label="Photos par mois"
            size="small"
            value={photos}
            onChange={(e) => setPhotos(e.target.value)}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Stack>
        <FormControlLabel
          control={
            <Checkbox
              checked={documentsPhoto}
              onChange={(e) => setDocumentsPhoto(e.target.checked)}
            />
          }
          label="Lecture des photos de documents"
        />
        <FormControlLabel
          control={<Checkbox checked={actif} onChange={(e) => setActif(e.target.checked)} />}
          label="Formule proposée"
        />
        {confirmerPrix && (
          <Typography color="text.secondary">
            Le nouveau tarif s’appliquera aux nouvelles abonnées tout de suite, et aux abonnées
            actuelles à leur première échéance au moins trente jours plus tard, après un courriel.
            Confirmer ?
          </Typography>
        )}
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={() => void enregistrer()}>
            {confirmerPrix ? 'Oui, appliquer ce tarif' : 'Enregistrer'}
          </Button>
          {confirmerPrix && (
            <Button color="inherit" onClick={() => setConfirmerPrix(false)}>
              Annuler
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

export function Formules() {
  const { direErreur } = useMessages();
  const [plans, setPlans] = useState<PlanAdmin[] | undefined>();

  useEffect(() => {
    api.admin.plans().then(setPlans).catch(direErreur);
  }, [direErreur]);

  if (!plans) return <Chargement />;
  return (
    <Stack spacing={2}>
      <Typography color="text.secondary">
        Les prix sont TTC, TVA non applicable (art. 293 B du CGI). Un prix chez Stripe ne se modifie
        jamais : chaque changement en crée un nouveau, et le prix payé par une abonnée est figé
        jusqu’au préavis.
      </Typography>
      {plans.map((p) => (
        <CartePlan
          key={p.cle}
          plan={p}
          onMaj={(maj) => setPlans((liste) => liste?.map((x) => (x.cle === maj.cle ? maj : x)))}
        />
      ))}
    </Stack>
  );
}
